import { Role } from '../enums';

/**
 * Иерархия ролей (BLUEPRINT 3.1): ADMIN ⊃ OPS ⊃ CAPTAIN ⊃ MEMBER.
 * Проверка на сервере в процедурах tRPC; клиент использует только для отображения.
 *
 * `VENUE` (Stage 14, ADR-27) в иерархию не входит: это сотрудник площадки, у него нет
 * ни отряда, ни пула. Ранг −1 — ниже участника, поэтому `hasRole(VENUE, MEMBER)` = false
 * и ни одна процедура участника ему не открыта; портал площадки проверяет роль отдельно.
 */
export const ROLE_RANK: Readonly<Record<Role, number>> = {
  [Role.VENUE]: -1,
  [Role.MEMBER]: 0,
  [Role.CAPTAIN]: 1,
  [Role.OPS]: 2,
  [Role.ADMIN]: 3,
};

/** true, если `actual` не ниже `required` в иерархии. */
export function hasRole(actual: Role, required: Role): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[required];
}

/** Роли, которым открыта админка. */
export const STAFF_ROLES: readonly Role[] = [Role.OPS, Role.ADMIN];

export function isStaff(role: Role): boolean {
  return hasRole(role, Role.OPS);
}

/** Сотрудник площадки: попадает в портал площадки, а не в админку. */
export function isVenueRole(role: Role): boolean {
  return role === Role.VENUE;
}

/** Кому вообще открывается приложение `apps/admin` (админка или портал площадки). */
export function canOpenAdminApp(role: Role): boolean {
  return isStaff(role) || isVenueRole(role);
}

/**
 * Города оператора (Stage 14, задача 1): `opsCityIds` ограничивает OPS своими городами,
 * пустой список — все города. У ADMIN ограничения нет никогда.
 */
export function cityScopeFor(
  role: Role,
  opsCityIds: readonly string[],
): readonly string[] | null {
  if (role !== Role.OPS) return null;
  return opsCityIds.length > 0 ? opsCityIds : null;
}

/** Виден ли город этому сотруднику. */
export function canSeeCity(
  role: Role,
  opsCityIds: readonly string[],
  cityId: string | null | undefined,
): boolean {
  const scope = cityScopeFor(role, opsCityIds);
  if (!scope) return true;
  return cityId !== null && cityId !== undefined && scope.includes(cityId);
}
